export interface Coordinate {
  latitude: number;
  longitude: number;
}

/**
 * Calculates the great-circle distance between two coordinates using the Haversine formula.
 *
 * @param coordinate1 - The first coordinate with latitude and longitude in decimal degrees.
 * @param coordinate2 - The second coordinate with latitude and longitude in decimal degrees.
 * @returns The distance between the two coordinates in kilometers.
 * @throws Will throw an error if the latitude is not in the range [-90, 90] or if the longitude is not in the range [-180, 180].
 */
export function getDistanceBetweenCoordinates(
  coordinate1: Coordinate,
  coordinate2: Coordinate
): number {
  const MAX_VALID_LATITUDE = 90;
  const MAX_VALID_LONGITUDE = 180;

  if (
    Math.abs(coordinate1.latitude) > MAX_VALID_LATITUDE ||
    Math.abs(coordinate2.latitude) > MAX_VALID_LATITUDE
  ) {
    throw new Error('Invalid latitude value');
  }

  if (
    Math.abs(coordinate1.longitude) > MAX_VALID_LONGITUDE ||
    Math.abs(coordinate2.longitude) > MAX_VALID_LONGITUDE
  ) {
    throw new Error('Invalid longitude value');
  }

  const EARTH_RADIUS_KM = 6371;

  const lat1 = toRadians(coordinate1.latitude);
  const lon1 = toRadians(coordinate1.longitude);

  const lat2 = toRadians(coordinate2.latitude);
  const lon2 = toRadians(coordinate2.longitude);

  return (
    2 *
    EARTH_RADIUS_KM *
    Math.asin(
      Math.sqrt(
        Math.pow(Math.sin((lat2 - lat1) / 2), 2) +
          Math.cos(lat1) *
            Math.cos(lat2) *
            Math.pow(Math.sin((lon2 - lon1) / 2), 2)
      )
    )
  );

  function toRadians(degrees: number): number {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return degrees * (Math.PI / 180);
  }
}
